/** @file downloadBundle: fetches a puppet's export zip (bundle or bake) and saves it via an `<a download>`. */
import type { ExportQuery } from '@zilo/pose3d-api/types/restAdded';
import type { EditorApi } from '../store/editorApi';

/** The client's export call with its query; EditorApi still types it with the id only (the adapter passes every argument). */
type ExportCall = (id: string, query?: ExportQuery) => Promise<Uint8Array>;

/** A file-name-safe version of the puppet name (letters, digits, `-`, `_`), `bundle` when nothing is left. */
const fileName = (name: string, baked: boolean): string => `${name.replace(/[^\w-]+/g, '_').replace(/^_+|_+$/g, '') || 'bundle'}${baked ? '-baked' : ''}.zip`;

/**
 * `<name>.zip` (the runtime bundle) or, for `{ format: 'baked', ... }`, `<name>-baked.zip`. Resolves once the browser
 * was handed the file; rejects with the client's error when the export fails.
 */
export async function downloadBundle(api: EditorApi, puppetId: string, name: string, query?: ExportQuery): Promise<void> {
  const call: ExportCall = api.puppets.exportBundle;
  const baked = query?.format === 'baked';
  const bytes = await (baked ? call(puppetId, query) : call(puppetId));
  const url = URL.createObjectURL(new Blob([bytes.slice()], { type: 'application/zip' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName(name, baked);
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => { URL.revokeObjectURL(url); }, 0);
}
